import ContentCurationList from '../content-curation/ContentCurationList'
import ContentCurationItem from '../content-curation/ContentCurationItem'
import ArticleIcon from '../assets/icon-content-type-article'
import QuestionIcon from '../assets/icon-content-type-question'
import AnswerIcon from '../assets/icon-content-type-answer'
import DiscussionIcon from '../assets/icon-content-type-discussion'
import ProductUpdateIcon from '../assets/icon-product-update-type'
import IdeaIcon from '../assets/icon-content-type-idea'
import { GetServerSideProps } from 'js/src/server-middleware/context'
import { z } from 'zod'
import Unified from '../../services/unified'
import classnames from 'classnames'
import WidgetContainer, { widgetContainerConfig } from '../widget-container'

interface CuratedTopicWithIconAndIdx {
  id: string
  type: 'conversation' | 'question' | 'article' | 'idea' | 'productUpdate'
  url: string
  title: string
  icon?: JSX.Element
  idx?: number
}

interface RecommendationsWidgetProps {
  config: RecommendationsWidgetConfig
  curatedTopics: CuratedTopicWithIconAndIdx[]
}

export const widgetName = 'recommendations'

export const config = z
  .object({
    curatedTopics: z.number().array()
  })
  .merge(widgetContainerConfig)

export type RecommendationsWidgetConfig = z.infer<typeof config>

function isCuratedContent(curatedTopic: CuratedTopicWithIconAndIdx | null): curatedTopic is CuratedTopicWithIconAndIdx {
  return !!curatedTopic
}

export const getServerSideProps: GetServerSideProps<RecommendationsWidgetConfig, RecommendationsWidgetProps> = async (
  context,
  config
) => {
  const unified = new Unified(context)
  const curatedTopics = await context.cache(
    `${JSON.stringify(config)}-${context.userId}`,
    async () => {
      return await unified.getTopicsIndexedByPublicId(config.curatedTopics)
    },
    60
  )

  return {
    config: config,
    curatedTopics: config.curatedTopics
      .map((topicId) => {
        const topic = curatedTopics[topicId]
        if (!topic) {
          return null
        }
        return {
          id: topic.id.toString(),
          url: topic.url,
          title: topic.title,
          type: topic.contentType
        }
      })
      .filter(isCuratedContent)
  }
}

const Recommendations = (props: RecommendationsWidgetProps) => {
  const { config, curatedTopics = [] } = props
  const topicType = {
    question: <QuestionIcon />,
    article: <ArticleIcon />,
    answer: <AnswerIcon />,
    discussion: <DiscussionIcon />,
    conversation: <DiscussionIcon />,
    productUpdate: <ProductUpdateIcon width={24} height={24} />,
    idea: <IdeaIcon />
  }

  if (!curatedTopics.length) {
    return null
  }

  return (
    <WidgetContainer config={config} widgetName='knowledge-base'>
      <div className={classnames('topic-curation__container', 'full-width', 'custom-featured-topic-list')}>
        <div className='sitewidth'>
          {curatedTopics.length ? (
            <ContentCurationList>
              {curatedTopics.map((item, idx) => {
                item.icon = topicType[item.type]
                item.idx = idx
                return <ContentCurationItem key={item.id} {...item} />
              })}
            </ContentCurationList>
          ) : null}
        </div>
      </div>
    </WidgetContainer>
  )
}

export default Recommendations
